import { ParsedMenuItem, ParsedModifierGroup } from '../../../types/menu';
import { getOptionModifierGroups } from '../../../utils/lazyOptionBuild';

/**
 * N single-pick groups that the centre pane draws as one repeated-slot panel
 * instead of N rows.
 *
 * Two kinds, told apart by {@link RepeatedSlotSet.keepsPositions}:
 * - interchangeable slots, e.g. Manhattan Bagel's "Bakers Dozen Bagels" with
 *   `bagel_choice_1` … `bagel_choice_13`. Any bagel may sit in any slot, so
 *   the panel keeps like options together;
 * - per-unit slots, e.g. a Crowd Pleaser's `chili_dog_selection_1..4` beside
 *   `onion_options_selection_1..4`, where `_2` means "for chili dog 2". The
 *   panel must never move a pick from one slot to another.
 */
export interface RepeatedSlotSet {
  /** The shared name with the `_N` suffix removed: `bagel_choice`. */
  stem: string;
  /** The slots in index order: `groups[0]` is `<stem>_1`. */
  groups: ParsedModifierGroup[];
  /**
   * Each slot's cart path, parallel to `groups`: the group id at the top level
   * (`bagel_choice_1`), or `<option path>__<group id>` below it
   * (`upsell_double_patty_selection_1__triple__extra_patty`).
   */
  paths: string[];
  /** Every slot must be filled (min 1), as opposed to "select up to 1". */
  isRequired: boolean;
  /**
   * `single`: one pick per unit — the panel (phases 1–2).
   * `multi` (phase 3): any number of picks per unit, up to `max` — the grid.
   */
  kind: 'single' | 'multi';
  /** Picks each unit must have: 1 for "select 1", 0 for optional sets. */
  min: number;
  /** Picks each unit may have: 1 for single sets, Infinity when unlimited. */
  max: number;
  /**
   * Slot N belongs to unit N. True when the item carries another `_1..N`
   * family of the same length — the menu numbering several things per unit.
   */
  keepsPositions: boolean;
  /**
   * Follow-up sets only (HITL-1722): which units the follow-up applies to.
   * A unit whose pick opens no such group keeps its column as an inert cell,
   * so cell N is unit N in every panel. Absent: every unit applies.
   */
  applicable?: boolean[];
  /** Follow-up sets only: the option(s) that opened it — `triple`. */
  prefix?: string;
  /**
   * Follow-up sets merged across options only: per unit, the option that
   * opened its group (`cheddar`), so each cell can say which it belongs to.
   */
  parentNames?: (string | undefined)[];
}

/**
 * Stands in, inside a slot array, for a unit a follow-up does not apply to.
 * Truthy, so "first empty slot" and Fill all step over it; never written.
 */
export const INAPPLICABLE_SLOT = '\u0000inapplicable';

/**
 * Below this a set is not worth a panel: a "pick 2" pair reads fine as two
 * rows, and the panel's tiles + strip would take more room than they save.
 */
export const MIN_REPEATED_SLOTS = 3;

const SLOT_NAME_PATTERN = /^(.+)_(\d+)$/;

/** `MOD_SEPARATOR`-joined cart path, the format RecursiveModGroup builds. */
export const joinSlotPath = (basePath: string, id: string | number) =>
  basePath ? [basePath, id].join('__') : String(id);

/**
 * The option a slot hangs under, as ModGroup derives `parentModifier`: the
 * second-to-last node, and only below the top level.
 */
export const getSlotParentModifier = (pathToItem: string) => {
  const nodes = pathToItem.split('__');
  return nodes.length > 2 ? nodes[nodes.length - 2] : '';
};

/** "Select 1" (required) or "select up to 1" (optional). */
const getSingleChoiceKind = (
  modGroup: ParsedModifierGroup
): 'required' | 'optional' | null => {
  if (modGroup.maximumSelections !== 1) return null;
  if (modGroup.minimumSelections === 1) return 'required';
  if (!modGroup.minimumSelections) return 'optional';
  return null;
};

// `uniqueName` first: the front end's own min=max>1 split
// (menuUnification.utils.ts) writes `<constraint>_<n>` there and keeps the bare
// constraint name in `name`. Brand-menu constraints that are separate on the
// menu carry the suffix in `id` as well, so the fallback covers them.
const getSlotName = (modGroup: ParsedModifierGroup) =>
  modGroup.uniqueName || String(modGroup.id);

const getOptionSignature = (modGroup: ParsedModifierGroup) =>
  Object.keys(modGroup.menuItems || {})
    .sort()
    .join('\u0000');

/** Does picking this option open further groups (`triple` → `extra_patty`)? */
export const optionOpensGroups = (option: ParsedMenuItem | undefined) =>
  Object.keys((option?.modifierGroups && getOptionModifierGroups(option)) ?? {})
    .length > 0;

/**
 * Finds the repeated-slot sets among ONE level of an item's modifier groups.
 *
 * A set is groups that all:
 * 1. are single-pick of the same kind — all "select 1" or all "select up to 1";
 * 2. are named `<stem>_<n>` with the same stem, the indices running 1..N with
 *    no gap or repeat;
 * 3. offer exactly the same options (options may open further groups: the
 *    panel draws those as follow-ups, see {@link buildFollowUpSets});
 * 4. number at least {@link MIN_REPEATED_SLOTS}.
 *
 * A stem that breaks any rule for any of its groups yields no set at all, so
 * the panel never takes over a set it would have to draw half of. Names alone
 * are not enough (two sauce slots can share a stem and differ in options), and
 * options alone are not enough (two unrelated groups can list the same ones),
 * which is why rules 2 and 3 are both required.
 *
 * Every `_1..N` family on the level counts towards `keepsPositions`, including
 * ones that are not a set themselves: a chili-dog family that stays as rows
 * still makes its onion family per-dog.
 */
export const findRepeatedSlotSets = (
  modGroups: ParsedModifierGroup[],
  /** The path of the option these groups hang under; '' at the top level. */
  basePath = ''
): RepeatedSlotSet[] => {
  const byStem = new Map<
    string,
    { index: number; modGroup: ParsedModifierGroup }[]
  >();

  modGroups.forEach((modGroup) => {
    const match = SLOT_NAME_PATTERN.exec(getSlotName(modGroup));
    if (!match) return;
    const [, stem, index] = match;
    const members = byStem.get(stem) || [];
    members.push({ index: Number(index), modGroup });
    byStem.set(stem, members);
  });

  // Every well-formed `_1..N` family, by stem, with its length.
  const families = new Map<
    string,
    { index: number; modGroup: ParsedModifierGroup }[]
  >();
  byStem.forEach((members, stem) => {
    const sorted = [...members].sort((a, b) => a.index - b.index);
    if (sorted.every(({ index }, position) => index === position + 1)) {
      families.set(stem, sorted);
    }
  });

  const sets: RepeatedSlotSet[] = [];
  families.forEach((sorted, stem) => {
    if (sorted.length < MIN_REPEATED_SLOTS) return;

    const first = sorted[0].modGroup;
    const singleKind = getSingleChoiceKind(first);
    // Phase 3: a family that takes any number of picks (no max, or a cap
    // above 1). Every unit has to share the same min and max.
    const isMulti =
      !singleKind &&
      first.maximumSelections !== 1 &&
      sorted.every(
        ({ modGroup }) =>
          modGroup.maximumSelections === first.maximumSelections &&
          (modGroup.minimumSelections ?? 0) === (first.minimumSelections ?? 0)
      );
    if (!singleKind && !isMulti) return;
    if (
      singleKind &&
      !sorted.every(
        ({ modGroup }) => getSingleChoiceKind(modGroup) === singleKind
      )
    )
      return;

    const signature = getOptionSignature(sorted[0].modGroup);
    if (!signature) return;
    if (
      !sorted.every(
        ({ modGroup }) => getOptionSignature(modGroup) === signature
      )
    )
      return;
    // Options that open further groups are fine (HITL-1722): the panel draws
    // their follow-ups under it — see buildFollowUpSets.

    const keepsPositions = Array.from(families).some(
      ([otherStem, other]) =>
        otherStem !== stem && other.length === sorted.length
    );

    if (isMulti) {
      // Only per-unit pick-any families (a sibling `_1..N` says unit N owns
      // slot N); interchangeable multi slots are out of scope. Options that
      // open further groups are fine (HITL-1728): the grid draws each ticked
      // option's follow-ups under its row — see buildGridFollowUpSets.
      if (!keepsPositions) return;
      const min = first.minimumSelections ?? 0;
      sets.push({
        stem,
        groups: sorted.map(({ modGroup }) => modGroup),
        paths: sorted.map(({ modGroup }) =>
          joinSlotPath(basePath, modGroup.id)
        ),
        isRequired: min > 0,
        kind: 'multi',
        min,
        max: first.maximumSelections > 0 ? first.maximumSelections : Infinity,
        keepsPositions,
      });
      return;
    }

    sets.push({
      stem,
      groups: sorted.map(({ modGroup }) => modGroup),
      paths: sorted.map(({ modGroup }) => joinSlotPath(basePath, modGroup.id)),
      isRequired: singleKind === 'required',
      kind: 'single',
      min: singleKind === 'required' ? 1 : 0,
      max: 1,
      keepsPositions,
    });
  });

  return sets;
};

/**
 * The one intervention line a repeated-slot write sends:
 * `Bakers Dozen Bagels: 6 Plain, 4 Everything, 2 Sesame, 1 of 13 empty`.
 * Options are listed in the order they first appear in `slots`, which the
 * panel keeps grouped, so the line reads the way the strip does. Empty slots
 * are only mentioned when there are some.
 */
export const describeRepeatedSlots = (
  itemName: string,
  slots: (string | undefined)[]
): string => {
  const counts = new Map<string, number>();
  let empty = 0;
  slots.forEach((name) => {
    if (!name) {
      empty += 1;
      return;
    }
    counts.set(name, (counts.get(name) || 0) + 1);
  });
  const parts = Array.from(counts, ([name, count]) => `${count} ${name}`);
  if (empty) parts.push(`${empty} of ${slots.length} empty`);
  return `${itemName}: ${parts.length ? parts.join(', ') : 'cleared'}`;
};

/** One slot's value: the selected option's `itemId`, or undefined if empty. */
export type SlotValue = string | undefined;

const countSlots = (slots: SlotValue[]) => {
  const counts = new Map<string, number>();
  slots.forEach((id) => {
    if (id) counts.set(id, (counts.get(id) || 0) + 1);
  });
  return counts;
};

/**
 * Lays `counts` out the way the panel's strip always reads: like options
 * together, in `optionOrder` (the tile order), empties last. Slots are
 * interchangeable, so this is what readback and the POS ticket print too.
 * An id missing from `optionOrder` keeps its place after the known ones
 * rather than being dropped.
 */
export const layoutSlots = (
  counts: Map<string, number>,
  optionOrder: string[],
  slotCount: number
): SlotValue[] => {
  const order = [
    ...optionOrder,
    ...Array.from(counts.keys()).filter((id) => !optionOrder.includes(id)),
  ];
  const laidOut: SlotValue[] = order.flatMap((id) =>
    Array(counts.get(id) || 0).fill(id)
  );
  return [
    ...laidOut,
    ...Array(Math.max(0, slotCount - laidOut.length)).fill(undefined),
  ].slice(0, slotCount);
};

/** `slots` regrouped, unchanged in content. */
export const groupSlots = (slots: SlotValue[], optionOrder: string[]) =>
  layoutSlots(countSlots(slots), optionOrder, slots.length);

/**
 * Every operation below works on positions first, then regroups unless the
 * set keeps positions (per-unit slots, where slot N is unit N's). Regrouped,
 * the result is the same as counting and laying out afresh.
 */
const settle = (
  slots: SlotValue[],
  optionOrder: string[],
  keepPositions: boolean
) => (keepPositions ? slots : groupSlots(slots, optionOrder));

/** One more `id` in the first empty slot. Null when no slot is empty. */
export const addToSlots = (
  slots: SlotValue[],
  id: string,
  optionOrder: string[],
  keepPositions = false
): SlotValue[] | null => {
  const empty = slots.findIndex((slot) => !slot);
  if (empty < 0) return null;
  return settle(
    slots.map((slot, index) => (index === empty ? id : slot)),
    optionOrder,
    keepPositions
  );
};

/** The last slot holding `id` emptied. Null when `id` is in no slot. */
export const removeFromSlots = (
  slots: SlotValue[],
  id: string,
  optionOrder: string[],
  keepPositions = false
): SlotValue[] | null => {
  const last = slots.lastIndexOf(id);
  if (last < 0) return null;
  return settle(
    slots.map((slot, index) => (index === last ? undefined : slot)),
    optionOrder,
    keepPositions
  );
};

/**
 * The slots at `indexes` set to `id` — or emptied, when `id` is undefined
 * (clearing a per-unit optional slot).
 */
export const paintSlots = (
  slots: SlotValue[],
  indexes: number[],
  id: SlotValue,
  optionOrder: string[],
  keepPositions = false
): SlotValue[] =>
  settle(
    slots.map((slot, index) => (indexes.includes(index) ? id : slot)),
    optionOrder,
    keepPositions
  );

/** Every empty slot set to `id`. */
export const fillEmptySlots = (
  slots: SlotValue[],
  id: string,
  optionOrder: string[],
  keepPositions = false
): SlotValue[] =>
  settle(
    slots.map((slot) => slot || id),
    optionOrder,
    keepPositions
  );

/** A single-pick group can be drawn as a panel; anything else stays a row. */
export const isSingleChoiceGroup = (modGroup: ParsedModifierGroup) =>
  getSingleChoiceKind(modGroup) !== null;

/**
 * HITL-1722 — the follow-ups a panel draws under itself.
 *
 * For every unit whose pick opens further groups, each nested group becomes a
 * cell in a follow-up set at `<slot path>__<option id>__<group id>`, the path
 * RecursiveModGroup would have used. Nested groups with the same name and the
 * same options MERGE into one follow-up whichever option opened them (american
 * cheese / cheddar / swiss cheese → one `mod_codes_sub`), each unit remembering
 * its own option and its own group.
 *
 * Every follow-up keeps the parent's N units — `applicable` marks the ones it
 * belongs to — so its strip lines up under the parent's, cell N = unit N.
 *
 * `slots` holds the parent's picks (itemIds), in unit order.
 */
export const buildFollowUpSets = (
  parent: RepeatedSlotSet,
  slots: SlotValue[],
  optionsById: Record<string, ParsedMenuItem>
): RepeatedSlotSet[] => {
  const unitCount = slots.length;
  const byKey = new Map<
    string,
    {
      nested: ParsedModifierGroup;
      units: (
        | { modGroup: ParsedModifierGroup; path: string; parentName: string }
        | undefined
      )[];
    }
  >();

  slots.forEach((id, unit) => {
    if (!id || id === INAPPLICABLE_SLOT) return;
    const option = optionsById[id];
    if (!optionOpensGroups(option)) return;
    const optionPath = joinSlotPath(
      parent.paths[unit],
      String(option.id ?? option.itemId)
    );
    Object.values(getOptionModifierGroups(option)).forEach((nested) => {
      const key = `${nested.name}\u0000${getOptionSignature(nested)}`;
      const entry = byKey.get(key) || {
        nested,
        units: Array(unitCount).fill(undefined),
      };
      entry.units[unit] = {
        modGroup: nested,
        path: joinSlotPath(optionPath, nested.id),
        parentName: option.name,
      };
      byKey.set(key, entry);
    });
  });

  return Array.from(byKey.values()).map(({ nested, units }) => {
    const parentNames = units.map((unit) => unit?.parentName);
    const distinctParents = Array.from(
      new Set(parentNames.filter(Boolean) as string[])
    );
    return {
      stem: nested.name,
      groups: units.map((unit) => unit?.modGroup ?? nested),
      paths: units.map((unit) => unit?.path ?? ''),
      isRequired: (nested.minimumSelections ?? 0) > 0,
      kind: 'single' as const,
      min: (nested.minimumSelections ?? 0) > 0 ? 1 : 0,
      max: 1,
      keepsPositions: true,
      applicable: units.map(Boolean),
      prefix: distinctParents.join('/'),
      parentNames: distinctParents.length > 1 ? parentNames : undefined,
    };
  });
};

/** One cell of a pick-any grid changing: `on` adds the option to the unit. */
export interface PickChange {
  unit: number;
  optionName: string;
  on: boolean;
}

/**
 * HITL-1726 — the one intervention line a grid gesture sends:
 * `crowd pleaser #16: +no pickles on 1, 2, 3; −add mayo on 4` (1-based units).
 */
export const describePickChanges = (
  itemName: string,
  changes: PickChange[]
): string => {
  const groups = new Map<string, number[]>();
  changes.forEach(({ unit, optionName, on }) => {
    const key = `${on ? '+' : '−'}${optionName}`;
    groups.set(key, [...(groups.get(key) || []), unit + 1]);
  });
  const parts = Array.from(
    groups,
    ([key, units]) => `${key} on ${units.sort((a, b) => a - b).join(', ')}`
  );
  return `${itemName}: ${parts.join('; ')}`;
};

/**
 * HITL-1728 — the follow-ups a pick-any grid draws under each option's row.
 *
 * One unit can tick several options that open the SAME group (bacon and
 * ketchup both open `mod_codes_sub`), so unlike buildFollowUpSets nothing
 * merges across options: every (ticked option O) × (each group G that O
 * opens) is its own follow-up, keyed by O's itemId. Each keeps the parent's N
 * units — `applicable` marks the ones that ticked O — at
 * `<unit path>__<O id>__<G id>`, the path RecursiveModGroup would have used.
 *
 * `picks` holds each unit's ticked itemIds, in unit order.
 */
export const buildGridFollowUpSets = (
  parent: RepeatedSlotSet,
  picks: string[][],
  options: ParsedMenuItem[]
): Map<string, RepeatedSlotSet[]> => {
  const byOption = new Map<string, RepeatedSlotSet[]>();
  options.forEach((option) => {
    if (!optionOpensGroups(option)) return;
    const applicable = picks.map((unitPicks) =>
      unitPicks.includes(option.itemId)
    );
    if (!applicable.some(Boolean)) return;
    const optionId = String(option.id ?? option.itemId);
    byOption.set(
      option.itemId,
      Object.values(getOptionModifierGroups(option)).map((nested) => {
        const min = nested.minimumSelections ?? 0;
        return {
          stem: nested.name,
          groups: picks.map(() => nested),
          paths: applicable.map((on, unit) =>
            on
              ? joinSlotPath(
                  joinSlotPath(parent.paths[unit], optionId),
                  nested.id
                )
              : ''
          ),
          isRequired: min > 0,
          kind: 'single' as const,
          min: min > 0 ? 1 : 0,
          max: 1,
          keepsPositions: true,
          applicable,
          prefix: option.name,
        };
      })
    );
  });
  return byOption;
};
